import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

interface NextActionItem {
  label: string;
  description: string;
  href?: string;
  onClick?: () => void;
  variant?: "default" | "outline" | "secondary";
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
}

interface NextActionsProps {
  title?: string;
  description?: string;
  actions: NextActionItem[];
  onNavigate?: () => void;
}

export function NextActions(props: Readonly<NextActionsProps>) {
  const {
    title = "Next Actions",
    description = "Recommended actions based on the current record state.",
    actions,
    onNavigate,
  } = props;

  return (
    <Card className="border-border/70">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {actions.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No recommended actions right now.
          </div>
        ) : (
          actions.map((action) => (
            <div
              key={action.label}
              className="flex flex-col gap-3 rounded-lg border bg-muted/20 p-3 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="space-y-1">
                <div className="text-sm font-medium">{action.label}</div>
                <div className="text-sm text-muted-foreground">
                  {action.description}
                </div>
              </div>
              {action.href ? (
                <Button
                  asChild
                  variant={action.variant || "outline"}
                  disabled={action.disabled}
                  className="shrink-0"
                >
                  <Link
                    to={action.href}
                    onClick={() => {
                      action.onClick?.();
                      onNavigate?.();
                    }}
                  >
                    {action.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              ) : (
                <Button
                  type="button"
                  variant={action.variant || "outline"}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  loading={action.loading}
                  loadingText={action.loadingText}
                  className="shrink-0"
                >
                  {action.label}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
